import { ArrowRight, Mic } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { AgendaGenerationTranscriptionMetadataPurpose } from "@elation/api-client/src/stitch-web";

import { AGENDA_ITEM_DESCRIPTION_MAX_LENGTH } from "@src/features/agenda/domain";
import {
  useTourGuideStep,
  VISIT_TOUR_ENCOUNTER_ID,
  VISIT_TOUR_ID,
  VISIT_TOUR_STEP_IDS,
  VisitTour,
} from "@src/features/tourguide";
import {
  buildVoiceInputButtonViewProps,
  useVoiceInput,
  VoiceAudioMeter,
  VoiceInputButtonView,
  type VoiceInputConfig,
} from "@src/features/voice-input";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";

import { GapListDropzoneV2 } from "../GapListDropzone/GapListDropzone";

const UNHANDLED_TOUR_ACTION_ERROR =
  "Tour add agenda item action was not handled";
const VISIT_TOUR_ADD_AGENDA_ITEM_DRAFT = "Follow up on lab results";

/**
 * `useVoiceInput` must be called unconditionally (hook rules) even though
 * voice input can only ever be *started* once `patientId` is known — the
 * mic control below renders as an inert, disabled icon (not wired to
 * `startRecording`) whenever `patientId` is `undefined`. It exists purely
 * to satisfy `VoiceInputConfig`'s type while `patientId` is unresolved.
 * `VisitPage` can render this composer before its `patientId` query
 * settles (`useGetPatientById(patientId ?? 0, { enabled: patientId != null
 * })`), and keeping `<Input>` mounted unconditionally across that
 * transition — rather than only mounting a voice-aware version of it once
 * `patientId` arrives — is what preserves the physician's typed draft and
 * cursor position (see the focus-management note below).
 *
 * "Can never start recording without a real `patientId`" does not by
 * itself guarantee `transcribe()` never reads this sentinel: unlike the
 * old `VoiceInputButton`-owned hook (which unmounted, and so aborted any
 * in-flight recording, the instant `patientId` went non-null → null), this
 * hook now lives in the parent and survives that transition. Without the
 * `cancelRecording` effect below, a recording started with a real
 * `patientId` that then goes `null` (or changes) mid-recording would keep
 * running against a *stale* config until the physician clicks Stop or the
 * max-duration timer fires — reading whatever `patientId` was captured at
 * record-start, or this sentinel if recording somehow started before one
 * was ever set. That effect is what makes this comment actually true.
 */
const UNREACHABLE_VOICE_INPUT_CONFIG: VoiceInputConfig = {
  purpose: AgendaGenerationTranscriptionMetadataPurpose.agendagenerate,
  metadata: { patientId: -1 },
};

export interface AgendaAddItemControlsProps {
  canEdit: boolean;
  encounterId?: number;
  patientId?: number;
  isGapListUploading?: boolean;
  onCreateAgendaItem: (description: string) => Promise<void>;
  onGapListFilesSelected: (files: File[]) => void;
}

export interface AgendaAddItemControlsViewProps {
  canEdit: boolean;
  canUseFasterInputs?: boolean;
  encounterId?: number;
  initialDraft?: string;
  isGapListUploading?: boolean;
  patientId?: number;
  onSubmit: (description: string) => Promise<void>;
  onGapListFilesSelected: (files: File[]) => void;
}

export function AgendaAddItemControlsView({
  canEdit,
  canUseFasterInputs = canEdit,
  encounterId,
  initialDraft = "",
  isGapListUploading = false,
  patientId,
  onSubmit,
  onGapListFilesSelected,
}: AgendaAddItemControlsViewProps) {
  const [draft, setDraft] = useState(initialDraft);

  // See `UNREACHABLE_VOICE_INPUT_CONFIG`'s comment above for why this is
  // safe to fall back to a config that can never actually be used.
  const voiceInputConfig: VoiceInputConfig = useMemo(
    () =>
      patientId != null
        ? {
            purpose:
              AgendaGenerationTranscriptionMetadataPurpose.agendagenerate,
            metadata: {
              patientId,
              ...(encounterId != null ? { encounterId } : {}),
            },
          }
        : UNREACHABLE_VOICE_INPUT_CONFIG,
    [patientId, encounterId],
  );

  // Lifted here (rather than left inside a `VoiceInputButton` the composer
  // just renders) because the composer needs the raw recording state and
  // live `MediaStream` too — it swaps its own text input for a real audio
  // meter while recording (see the `isRecording` branch below), not just
  // the mic button's icon. Both the input row and the button below read
  // from this single hook instance, so they can never fall out of sync the
  // way two independent `useVoiceInput` calls could.
  const voiceInput = useVoiceInput({
    config: voiceInputConfig,
    onTranscript: () => {
      toast.success("Creating agenda items from voice");
    },
  });
  const isRecording = voiceInput.state === "recording";
  // Deliberately NOT unified with `isRecording` above, even though both are
  // booleans derived from `voiceInput.state`: `isRecording` gates the
  // handleSubmit guard, the Add button's `disabled`, and the audio-meter/
  // input-row swap below, all of which only care about the RECORDING state
  // specifically (the text input is back on screen during `transcribing`/
  // `processing`, and typing + submitting a normal agenda item then is
  // legitimate). `isVoiceSessionActive` is only for the patient-change
  // cancellation effect below, which must cover every state where a voice
  // session is still doing work — including `transcribing` and
  // `processing`, where there's an in-flight request that must not
  // complete into a different patient's context. `error` is deliberately
  // excluded: `cancelRecording` dispatches `COMPLETE`, which moves state to
  // `idle` unconditionally but does not clear the `error` string, so
  // calling it from `error` would leave a stale error message paired with
  // an `idle` state.
  const isVoiceSessionActive =
    voiceInput.state === "recording" ||
    voiceInput.state === "transcribing" ||
    voiceInput.state === "processing";

  // See `UNREACHABLE_VOICE_INPUT_CONFIG`'s comment: `useVoiceInput` living
  // here instead of inside a `VoiceInputButton` that unmounts with
  // `patientId` means a recording no longer gets aborted for free when
  // `patientId` disappears — or changes — mid-recording. Cancel explicitly
  // instead of letting it run against a config that no longer matches
  // reality.
  //
  // A same-tick `patientId` swap between two non-null patients (not just a
  // transition through `null`) has to be caught here too: this is a
  // PHI-boundary concern, not just a state bug. `VisitV2Route`'s
  // `useGetEncounterById` has no `placeholderData`, so an A→B navigation
  // *normally* passes `patientId` through `undefined` for one render, which
  // the `patientId == null` check below already catches. But
  // `queryClient.ts` doesn't override the default 5-minute `gcTime` — if
  // encounter B was visited recently enough to still be cached, React Query
  // serves it immediately and `patientId` jumps straight from A to B with
  // no intermediate `undefined` render. Left unhandled, a recording started
  // for patient A would keep running and later be transcribed into patient
  // B's context.
  //
  // `previousNonNullPatientIdRef` tracks the last non-null `patientId` this
  // effect has seen so it can distinguish "the patient actually changed"
  // from "this effect reran for an unrelated reason" (e.g.
  // `isVoiceSessionActive` flipping) — an incidental rerun must not cancel a
  // recording that just started for the current patient. It starts seeded
  // with the initial `patientId` so mount itself never counts as a change.
  const previousNonNullPatientIdRef = useRef(patientId);
  useEffect(() => {
    const previousPatientId = previousNonNullPatientIdRef.current;
    const patientChanged =
      previousPatientId != null &&
      patientId != null &&
      patientId !== previousPatientId;

    if (patientId != null) {
      previousNonNullPatientIdRef.current = patientId;
    }

    if ((patientId == null || patientChanged) && isVoiceSessionActive) {
      voiceInput.cancelRecording();
    }
    // `cancelRecording` specifically (not the whole `voiceInput` object,
    // which is a fresh literal every render) — it's a stable `useCallback`
    // from `useVoiceInput`, so this only reruns when `patientId`/
    // `isVoiceSessionActive` actually change.
  }, [patientId, isVoiceSessionActive, voiceInput.cancelRecording]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const description = draft.trim();
    // `isRecording` here (not just on the `Button`'s `disabled` prop below)
    // so "no submit while recording" holds regardless of how submission is
    // triggered — the button being disabled is the normal path, but this
    // guard is what actually enforces the invariant.
    if (!description || !canEdit || isRecording) return;

    setDraft("");
    void onSubmit(description).catch(() => {
      setDraft((current) => current || description);
    });
  };

  return (
    // Reference prototype (`visit.tsx:847-889`) puts `borderBottom` on this
    // row, not `borderTop`: with the composer now pinned *above* the dock
    // (see Agenda.tsx), this row's bottom edge is what needs the divider,
    // and the dock — now the true bottom-most element of the pinned region
    // — owns the region's bottom padding instead of this row (see
    // `AgendaDock`'s root `className` comment). The upward drop shadow this
    // row had is gone too: it existed because this used to be the
    // bottom-most element of the pinned region; it no longer is.
    <div className="shrink-0 border-border border-b bg-background py-3">
      <VisitTour.AddAgendaItemStep>
        {/*
         * Nested inside AddAgendaItemStep rather than replacing it: the
         * upload/mic controls that used to anchor FasterInputsStep in their
         * own row (below) are now inline in this same pill, so there is no
         * longer a standalone element to give FasterInputsStep as a target.
         * Both tour steps now anchor to the whole composer pill. Checked
         * `@src/features/tourguide`'s `Tour` component first: an unregistered
         * or unmounted target doesn't throw or render detached — `TourPopover`
         * falls back to a centered dialog ("The target for this tour step is
         * not visible. You can continue the tour or skip it.") — so simply
         * dropping FasterInputsStep's wrapper would have degraded gracefully
         * rather than broken. Anchoring both steps to the pill instead avoids
         * that degraded fallback entirely and keeps a real (if less precise)
         * spotlight for both steps.
         */}
        <VisitTour.FasterInputsStep>
          {/*
           * Known floor: this pill overflows below roughly a 260px container.
           * Its content has an irreducible width — dropzone 32 + gaps 12 +
           * the input's own padding floor 12 + the voice button's 128px cap
           * (it is `shrink-0`, so it cannot yield) + submit 32 + this form's
           * p-1 padding 8. Accepted rather than fixed: Chrome's side panel
           * floors near 320px, which after the row's 36px of horizontal
           * padding leaves ~284px, so nothing today gets that narrow. If a
           * host ever does, shrink the voice button's max-width so it can
           * yield — that is the only element here with slack to give.
           */}
          <form
            className="flex items-center gap-1 rounded-2xl border border-input bg-background p-1 transition-shadow focus-within:ring-2 focus-within:ring-ring/40"
            onSubmit={handleSubmit}
          >
            <GapListDropzoneV2
              disabled={!canUseFasterInputs}
              isUploading={isGapListUploading}
              onFilesSelected={onGapListFilesSelected}
              variant="compact"
            />

            {isRecording ? (
              // Reference prototype (`patientWorkspace.tsx:213-217`) swaps
              // the whole input for the audio meter + status text rather
              // than overlaying it — matched here. Focus is never at risk
              // in this swap: reaching `isRecording` requires the mic
              // button (a separate element) to have just been clicked, so
              // the `<Input>` being removed here can never itself hold
              // focus at this transition (verified there's no other code
              // path that starts recording). `min-w-0` + the meter's own
              // `shrink`/`overflow-hidden` let the decorative bars give up
              // space first in a narrow container; "Listening…" is the
              // real information here, so it stays `whitespace-nowrap` and
              // is never the thing that clips.
              <div className="flex h-8 min-w-0 flex-1 items-center gap-2 px-1.5">
                <VoiceAudioMeter
                  active={isRecording}
                  getMediaStream={voiceInput.getMediaStream}
                />
                {/*
                 * `role="status"` + `aria-live="polite"` per
                 * apps/web/docs/accessibility.md's live-region pattern.
                 * `VoiceInputButtonView` below also keeps its own
                 * `sr-only` status span ("Recording voice input") —
                 * that's a second, near-simultaneous polite announcement
                 * on this same transition. Left as-is rather than
                 * suppressing one: both are "polite" (non-interrupting),
                 * and de-duplicating them would couple this component to
                 * VoiceInputButtonView's internal status wiring for a
                 * minor redundancy, not a contradiction.
                 */}
                <span
                  aria-atomic="true"
                  // `role="status"` only takes its accessible name from
                  // `aria-label`/`aria-labelledby` ("name from author", not
                  // "name from content" per the ARIA spec) — without this,
                  // the name is empty and `getByRole("status", { name: ...
                  // })` can't tell this apart from VoiceInputButtonView's
                  // own hidden status span below. Set to match the visible
                  // text exactly so the accessible name and what's painted
                  // on screen never disagree.
                  aria-label="Listening…"
                  aria-live="polite"
                  className="shrink-0 whitespace-nowrap font-mono text-[10px] uppercase tracking-wide text-primary"
                  role="status"
                >
                  Listening…
                </span>
              </div>
            ) : (
              <Input
                aria-label="Agenda item"
                className="h-8 min-w-0 flex-1 border-0 bg-transparent px-1.5 text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 md-stitch:text-[13px]"
                disabled={!canEdit}
                maxLength={AGENDA_ITEM_DESCRIPTION_MAX_LENGTH}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={
                  canEdit
                    ? "Type an agenda item"
                    : "Add a patient to start the agenda"
                }
                type="text"
                value={draft}
              />
            )}

            {patientId != null ? (
              <VoiceInputButtonView
                {...buildVoiceInputButtonViewProps(voiceInput, {
                  disabled: !canUseFasterInputs,
                  // Shortened from "Creating agenda items..." — this pill is
                  // narrow enough (down to Chrome extension side-panel width)
                  // that the full phrase must be truncated by
                  // VoiceInputButtonView's max-width, and "Creating agenda
                  // i…" reads worse than a label that already fits. "agenda"
                  // is redundant here anyway: the composer it lives in only
                  // ever creates agenda items.
                  labels: { processing: "Creating items..." },
                })}
                hideLabel
                iconClassName="size-4"
              />
            ) : (
              <span className="inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground opacity-45">
                <Mic aria-hidden="true" className="size-4" />
                <span className="sr-only">Speak</span>
              </span>
            )}

            <Button
              aria-label="Add"
              className="size-8 shrink-0 rounded-full p-0"
              // `isRecording` matters here too: `draft` is never cleared
              // when recording starts (see the `isRecording` branch above —
              // it only swaps the visible `<Input>` for the audio meter),
              // so text typed before the mic was clicked survives,
              // invisibly, in state. `useVoiceInput`'s `onTranscript`
              // callback above never writes into `draft` either — the
              // "agendagenerate" purpose creates agenda items directly from
              // the transcript server-side, so voice input doesn't
              // replace/append `draft` the way it might in a dictation UI.
              // Given that, clearing `draft` on recording start would just
              // destroy a physician's typed text for no corresponding
              // benefit; disabling submission while recording instead
              // preserves it (it reappears, still editable, once recording
              // ends) while preventing a click here from submitting a
              // stale, no-longer-visible draft as a new agenda item.
              disabled={!canEdit || !draft.trim() || isRecording}
              type="submit"
            >
              <ArrowRight aria-hidden="true" className="size-4" />
            </Button>
          </form>
        </VisitTour.FasterInputsStep>
      </VisitTour.AddAgendaItemStep>
    </div>
  );
}

export function AgendaAddItemControls({
  canEdit,
  encounterId,
  patientId,
  isGapListUploading = false,
  onCreateAgendaItem,
  onGapListFilesSelected,
}: AgendaAddItemControlsProps) {
  const addAgendaItemTourStep = useTourGuideStep(
    VISIT_TOUR_ID,
    VISIT_TOUR_STEP_IDS.addAgendaItem,
  );
  const isVisitTourEncounter = encounterId === VISIT_TOUR_ENCOUNTER_ID;

  if (isVisitTourEncounter) {
    return (
      <AgendaAddItemControlsView
        canEdit={true}
        canUseFasterInputs={false}
        encounterId={VISIT_TOUR_ENCOUNTER_ID}
        initialDraft={
          addAgendaItemTourStep.isActive ? VISIT_TOUR_ADD_AGENDA_ITEM_DRAFT : ""
        }
        isGapListUploading={isGapListUploading}
        key={addAgendaItemTourStep.isActive ? "visit-tour-add" : "visit-tour"}
        patientId={patientId}
        onSubmit={async (description) => {
          const handled = await addAgendaItemTourStep.actions.addAgendaItem?.({
            encounterId,
            description,
          });

          if (!handled) {
            throw new Error(UNHANDLED_TOUR_ACTION_ERROR);
          }
        }}
        onGapListFilesSelected={onGapListFilesSelected}
      />
    );
  }

  return (
    <AgendaAddItemControlsView
      canEdit={canEdit}
      encounterId={encounterId}
      isGapListUploading={isGapListUploading}
      key="default"
      patientId={patientId}
      onSubmit={onCreateAgendaItem}
      onGapListFilesSelected={onGapListFilesSelected}
    />
  );
}
